import { ReactElement, ReactNode } from "react";
import { Badge, Box, Flex, Text } from "@chakra-ui/react";
import { NotificationSettingInfo } from "./NotificationSettingInfo";
import { useModifiedTranslation } from "../../../hooks/useModifiedTranslation";

/**
 * チェックの横に「i」を置く行に付ける class。
 * Chakra の Checkbox は全体が label なので、「i」のボタンは中に入れられず、横に並べる
 */
export const INFO_ROW_CLASS = "notification-setting-info-row";

/*
 * 通知の受信設定の区分。見出しの下のカードに、チェックやラジオを区切り線で並べる。
 * 見出しの横の「i」を押すと、区分の説明を下から出るシートで見せる。
 * 企業の値に従っている設定の区分は、見出しにタグ「企業の設定値」を出す。
 * アプリ全体の文字は太字なので、カードの中の文言は通常の太さに戻す。
 * チェックとラジオは既定では青なので、カードの中だけテーマの色で塗る。
 * Chakra の Radio は幅と余白以外の指定を丸の部分に渡し、行に線を引けないので、行の見た目はここで決める
 */
export function NotificationSettingSection(
  props: Readonly<{
    title: string;
    description: string;
    followsCompany: boolean;
    children: ReactNode;
    testId?: string;
  }>,
): ReactElement {
  const { title, description, followsCompany, children, testId } = props;
  const t = useModifiedTranslation();
  return (
    <Box mt={6} data-testid={testId}>
      <Flex align="center" mb={2}>
        <Text fontWeight="bold">{title}</Text>
        <NotificationSettingInfo
          title={title}
          description={description}
          testId={testId}
        />
        {followsCompany && (
          <Badge data-testid={testId && `${testId}-company-badge`}>
            {t("NotificationSetting.companySettingBadge")}
          </Badge>
        )}
      </Flex>
      <Box
        rounded="2xl"
        bg="primary.bg_lv1"
        px={4}
        py={1}
        fontWeight="normal"
        sx={{
          [`.chakra-checkbox, .chakra-radio, .${INFO_ROW_CLASS}`]: {
            w: "full",
            py: 3,
            borderBottomWidth: "1px",
            borderColor: "chakra-border-color",
          },
          [`.chakra-checkbox:last-child, .chakra-radio:last-child, .${INFO_ROW_CLASS}:last-child`]:
            {
              borderBottomWidth: 0,
            },
          // 「i」の下まで線を引くため、線は行に引き、中のチェックには引かない
          [`.${INFO_ROW_CLASS} > .chakra-checkbox`]: {
            py: 0,
            borderBottomWidth: 0,
          },
          ".chakra-checkbox__control[data-checked], .chakra-radio__control[data-checked]":
            {
              bg: "primary.theme_lv1",
              borderColor: "primary.theme_lv1",
              // 既定ではマウスが乗ると濃い青になる。スマホでは押した後も乗ったままになるので、同じ色にする
              _hover: {
                bg: "primary.theme_lv1",
                borderColor: "primary.theme_lv1",
              },
            },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
